/**
 * useProductSource — ViewModel for /products/source.
 *
 * Loads ALL products with their per-product Make/Buy flag and toggles it.
 * No product picker, no supplier/pricing (migration 0060). Each toggle
 * returns the refreshed product list so the screen re-renders from one source.
 */

import { useCallback, useEffect, useState } from 'react'
import { useAuth } from '../context/AuthContext'
import { APIROUTES } from '../constants/routes.constants'
import { parseApiError } from '../utils/api/parseApiError'
import type { ProductSourceDto, SourceType } from '../models/product-source.model'

export type { ProductSourceDto, SourceType } from '../models/product-source.model'

const BASE = APIROUTES.PRODUCTSOURCE

type SetResult = { ok: true } | { ok: false; message: string }

export interface UseProductSourceReturn {
  readonly products: ProductSourceDto[]
  readonly isLoading: boolean
  readonly error: string | null
  readonly reload: () => void
  readonly setSource: (productId: string, type: SourceType) => Promise<SetResult>
}

interface ListBody {
  readonly success: true
  readonly data: { readonly products: ProductSourceDto[] }
}

export function useProductSource(): UseProductSourceReturn {
  const { authedFetch } = useAuth()
  const [products, setProducts] = useState<ProductSourceDto[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [tick, setTick] = useState(0)

  useEffect(() => {
    let cancelled = false
    setIsLoading(true)
    setError(null)
    void (async () => {
      try {
        const res = await authedFetch(BASE)
        if (!res.ok) throw new Error((await parseApiError(res)).message)
        const body = (await res.json()) as ListBody
        if (!cancelled) setProducts(body.data.products)
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load products')
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [authedFetch, tick])

  const reload = useCallback(() => setTick((n) => n + 1), [])

  const setSource = useCallback(
    async (productId: string, type: SourceType): Promise<SetResult> => {
      const res = await authedFetch(`${BASE}/products/${encodeURIComponent(productId)}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sourceType: type })
      })
      if (!res.ok) return { ok: false, message: (await parseApiError(res)).message }
      const body = (await res.json()) as ListBody
      setProducts(body.data.products)
      return { ok: true }
    },
    [authedFetch]
  )

  return { products, isLoading, error, reload, setSource }
}
